import {isUIService} from '@github-ui/runtime-environment'
import {useEffect} from 'react'

// NOTE: React hook equivalent of the global side-effect imports that power the
// accessible animated GIF player on dotcom:
//  - @github-ui/animated-image-element/animated-image-behavior registers a
//    selector-observer that wraps animated GIFs in an <animated-image> element
//  - @github-ui/animated-image-element/element-entry registers the
//    <animated-image> custom element that renders the play/pause controls
// In the UI Service neither module is loaded, so animated GIFs autoplay and
// never receive the accessible player. This hook lazily loads both when the
// markdown container actually contains an animated image.

// Server markdown emits `<img data-animated-image>` for animated GIFs (see the
// dotcom selector in behaviors/animated-image.ts).
const ANIMATED_IMAGE_SELECTOR = 'img[data-animated-image]'

let registrationPromise: Promise<unknown> | null = null

function ensureAnimatedImageRegistered(): Promise<unknown> {
  // The behavior sets up a global selector-observer and must be loaded even if
  // the custom element was registered by the global element registry.
  if (customElements.get('animated-image')) {
    registrationPromise ||= import('@github-ui/animated-image-element/animated-image-behavior')
    return registrationPromise
  }

  // Both imports are one-time side effects. Only import them once per page.
  //
  // Dynamic imports also keep SSR from statically tracing these DOM-only
  // modules into the server bundle.
  registrationPromise ||= Promise.all([
    import('@github-ui/animated-image-element/animated-image-behavior'),
    import('@github-ui/animated-image-element/element-entry'),
  ])
  return registrationPromise
}

/**
 * Wraps animated GIFs in the accessible `<animated-image>` player when the
 * current markdown container includes any. No-op outside the UI Service, where
 * the behavior and element are already loaded globally.
 */
export function useAnimatedImageEnrichment(
  containerRef: React.RefObject<HTMLDivElement | null>,
  contentVersion?: unknown,
) {
  useEffect(() => {
    if (!isUIService) return
    const container = containerRef.current
    if (!container) return
    if (!container.querySelector(ANIMATED_IMAGE_SELECTOR)) return

    let cancelled = false
    void (async () => {
      await ensureAnimatedImageRegistered()
      if (cancelled) return
    })()

    return () => {
      cancelled = true
    }
  }, [containerRef, contentVersion])
}
